import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const getEndpoint = () =>
  window.location.protocol === 'http:'
    ? 'http://localhost:5001'
    : `https://${window.location.host}/api`;

const authHeaders = () => {
  try {
    const userData = JSON.parse(sessionStorage.getItem('clinic-application-jwt'));
    return userData?.token ? { Authorization: `Bearer ${userData.token}` } : {};
  } catch (e) {
    return {};
  }
};

export const LINKED_GROUPS = [
  {
    value: 'diagnosis',
    label: 'Main & Sub Diagnosis',
    parentLabel: 'Main Diagnosis',
    childLabel: 'Sub Diagnosis',
  },
  {
    value: 'industry',
    label: 'Industry & Profession',
    parentLabel: 'Industry',
    childLabel: 'Profession',
  },
];

export const fetchLinkedDropdown = async (group) => {
  const res = await axios.get(`${getEndpoint()}/dropdown/linked`, {
    headers: authHeaders(),
    params: { group },
  });
  return res.data?.data || [];
};

const post = async (path, body) => {
  const res = await axios.post(`${getEndpoint()}/dropdown/linked/${path}`, body, {
    headers: authHeaders(),
  });
  return res.data;
};

export const addLinkedParent = (group, parent) => post('parent', { group, parent });
export const editLinkedParent = (group, old_parent, new_parent) =>
  post('parent/edit', { group, old_parent, new_parent });
export const deleteLinkedParent = (group, parent) => post('parent/delete', { group, parent });

export const addLinkedChild = (group, parent, child) => post('child', { group, parent, child });
export const editLinkedChild = (group, parent, old_child, new_child) =>
  post('child/edit', { group, parent, old_child, new_child });
export const deleteLinkedChild = (group, parent, child) =>
  post('child/delete', { group, parent, child });

// Reads an error the API sent back, so the toast says what actually went wrong.
export const linkedError = (error, fallback) =>
  error?.response?.data?.message || fallback;

/**
 * Loads one linked group and exposes it the way a form needs it: the parent
 * options, and a lookup from a parent to its children.
 */
export const useLinkedDropdown = (group) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await fetchLinkedDropdown(group));
    } catch (error) {
      console.error(`Error fetching linked dropdown "${group}":`, error);
    } finally {
      setLoading(false);
    }
  }, [group]);

  useEffect(() => {
    reload();
  }, [reload]);

  const parents = useMemo(() => rows.map((row) => row.parent).filter(Boolean), [rows]);

  const childrenOf = useCallback(
    (parent) => rows.find((row) => row.parent === parent)?.children || [],
    [rows]
  );

  return { rows, parents, childrenOf, loading, reload };
};

/**
 * What the child select should show, and what it should hold, once a parent is
 * picked. With no parent - or a parent nobody has given children yet - the flat
 * list stays in play so the select is never empty.
 *
 * Returns { options, value }: a single child auto-selects, a child that does not
 * belong to the new parent is dropped.
 */
export const resolveChildSelection = (children, fallbackOptions, currentValue) => {
  const options = children?.length ? children : fallbackOptions || [];

  if (!children?.length) return { options, value: currentValue || '' };
  if (children.length === 1) return { options, value: children[0] };
  if (currentValue && !children.includes(currentValue)) return { options, value: '' };

  return { options, value: currentValue || '' };
};
